import * as React from "react";
import { Link, Navigate, useParams } from "react-router-dom";
import {
  PageHeader,
  PageHeaderTop,
  PageHeaderTitle,
  PageHeaderHeading,
  PageHeaderDescription,
  PageHeaderActions,
  Button,
  Badge,
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  Sheet,
  SheetClose,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
  StatusPill,
  Input,
  Label,
  cn,
  toast,
  type StatusPillStatus,
} from "@eduviaos/ui-kit";
import {
  Archive,
  ArrowLeft,
  CalendarRange,
  CheckCircle2,
  ChevronRight,
  Edit3,
  ExternalLink,
  RefreshCw,
  ShieldAlert,
  Sparkles,
} from "lucide-react";
import type {
  ApplyManualOverrideRequest,
  SchedulingWeekday,
  TimetableSlotView,
  TimetableStatusValue,
} from "@eduviaos/shared";
import {
  useApplyManualOverride,
  useArchiveTimetable,
  usePublishTimetable,
  useTimetableDetail,
} from "@/hooks/useTimetable";

/**
 * Phase G3 FE-1b — IA timetable weekly grid editor.
 *
 * Renders one timetable's slots on a weekday × period grid. Each cell
 * surfaces its subject / teacher / room and a manual-override
 * affordance — clicking a cell opens a side sheet that wraps the
 * BE-2c PATCH `/timetables/{id}/slots/{slot_id}` route.
 *
 * The page is split-pane:
 *   - **Left** (main): the grid itself — sticky header row of
 *     weekdays, sticky leftmost column of period times. Cells colour
 *     by status (lab periods get an amber stripe; manual overrides
 *     get a blue tag).
 *   - **Right** (aside): timetable summary card — status, objective
 *     score, source-job link, KPIs (sections / teachers / slots),
 *     and the publish / archive CTAs. The aside collapses below
 *     the grid on `< lg` viewports for tablet usability.
 *
 * Why a Sheet (not a Dialog) for the override editor: sheets cohabit
 * with the underlying grid so the IA can compare adjacent cells
 * while editing. Sheets also handle long content (we may grow the
 * editor with conflict-warning copy in G3 FE-2) without forcing a
 * scroll-locked modal.
 *
 * Submit-new-job flow lands in G3 FE-1d (constraint sheet).
 */
export function TimetableDetailPage() {
  const { timetableId } = useParams<{ timetableId: string }>();
  const detail = useTimetableDetail(timetableId);
  const publish = usePublishTimetable();
  const archive = useArchiveTimetable();
  const apply = useApplyManualOverride();

  const [editing, setEditing] = React.useState<TimetableSlotView | null>(null);
  const [filterTeacherId, setFilterTeacherId] = React.useState<string | null>(null);

  if (!timetableId) {
    return <Navigate to="/timetable" replace />;
  }

  if (detail.isLoading) {
    return <DetailSkeleton />;
  }

  if (detail.isError || !detail.data) {
    return <DetailError onRetry={() => void detail.refetch()} />;
  }

  const { timetable, slots } = detail.data;
  const grid = buildWeeklyGrid(slots);
  const teacherStats = computeTeacherStats(slots);
  const visibleTeacherId = filterTeacherId ?? null;
  const canPublish = timetable.status === "draft";
  const canArchive = timetable.status === "draft" || timetable.status === "published";
  const isPublishing = publish.isPending && publish.variables?.timetableId === timetable.id;
  const isArchiving = archive.isPending && archive.variables?.timetableId === timetable.id;

  return (
    <div className="space-y-6">
      <PageHeader>
        <PageHeaderTop>
          <PageHeaderTitle>
            <Button variant="ghost" size="sm" asChild className="self-start">
              <Link to="/timetable">
                <ArrowLeft aria-hidden="true" className="size-4" />
                Back to timetables
              </Link>
            </Button>
            <PageHeaderHeading className="flex items-center gap-3">
              {timetable.name}
              <StatusPill
                status={STATUS_PILL[timetable.status]}
                label={STATUS_LABEL[timetable.status]}
              />
            </PageHeaderHeading>
            <PageHeaderDescription>
              {timetable.description ??
                "No description set. Use the action menu on the list to rename or reschedule."}
            </PageHeaderDescription>
          </PageHeaderTitle>
          <PageHeaderActions>
            <Button
              variant="ghost"
              size="sm"
              onClick={() => void detail.refetch()}
              disabled={detail.isFetching}
            >
              <RefreshCw
                aria-hidden="true"
                className={cn("size-4", detail.isFetching && "animate-spin")}
              />
              Refresh
            </Button>
            {canPublish ? (
              <Button
                variant="default"
                size="sm"
                onClick={() =>
                  publish.mutate(
                    { timetableId: timetable.id },
                    {
                      onSuccess: () => {
                        toast.success(`Published "${timetable.name}".`);
                      },
                      onError: (caught) => {
                        const message = caught instanceof Error ? caught.message : String(caught);
                        toast.error(message || "Could not publish — please try again.");
                      },
                    },
                  )
                }
                disabled={isPublishing}
              >
                <CheckCircle2 aria-hidden="true" className="size-4" />
                {isPublishing ? "Publishing…" : "Publish"}
              </Button>
            ) : null}
            {canArchive ? (
              <Button
                variant="outline"
                size="sm"
                onClick={() =>
                  archive.mutate(
                    { timetableId: timetable.id },
                    {
                      onSuccess: () => {
                        toast.success(`Archived "${timetable.name}".`);
                      },
                      onError: (caught) => {
                        const message = caught instanceof Error ? caught.message : String(caught);
                        toast.error(message || "Could not archive — please try again.");
                      },
                    },
                  )
                }
                disabled={isArchiving}
              >
                <Archive aria-hidden="true" className="size-4" />
                {isArchiving ? "Archiving…" : "Archive"}
              </Button>
            ) : null}
          </PageHeaderActions>
        </PageHeaderTop>
      </PageHeader>

      <TeacherFilterBar
        timetableId={timetable.id}
        teacherStats={teacherStats}
        selectedTeacherId={visibleTeacherId}
        onSelect={(id) => setFilterTeacherId(id)}
      />

      <div className="grid gap-6 lg:grid-cols-[1fr_320px]">
        <WeeklyGrid
          grid={grid}
          isReadOnly={timetable.status === "archived"}
          highlightedTeacherId={visibleTeacherId}
          onSelectSlot={(slot) => setEditing(slot)}
        />
        <SummaryAside
          timetableId={timetable.id}
          totalSlots={timetable.total_slots}
          teacherCount={timetable.teacher_count}
          sectionCount={timetable.section_count}
          objectiveScore={timetable.objective_score}
          sourceJobId={timetable.source_job_id}
          publishedAt={timetable.published_at}
          archivedAt={timetable.archived_at}
          selectedTeacher={
            visibleTeacherId
              ? (teacherStats.find((t) => t.teacherId === visibleTeacherId) ?? null)
              : null
          }
        />
      </div>

      <SlotOverrideSheet
        slot={editing}
        onClose={() => setEditing(null)}
        onSubmit={async (payload) => {
          if (!editing) return;
          await apply.mutateAsync({
            timetableId: timetable.id,
            slotId: editing.id,
            payload,
          });
          toast.success(
            `Override applied to ${WEEKDAY_SHORT[editing.weekday]} · Period ${editing.period_index + 1}.`,
          );
          setEditing(null);
        }}
        submitting={apply.isPending}
      />
    </div>
  );
}

// ── Weekly grid ──────────────────────────────────────────────────────

const WEEKDAY_ORDER: SchedulingWeekday[] = ["MON", "TUE", "WED", "THU", "FRI", "SAT", "SUN"];

const WEEKDAY_SHORT: Record<SchedulingWeekday, string> = {
  MON: "Mon",
  TUE: "Tue",
  WED: "Wed",
  THU: "Thu",
  FRI: "Fri",
  SAT: "Sat",
  SUN: "Sun",
};

interface BuiltGrid {
  /** Sorted ascending — only weekdays that actually have slots. */
  weekdays: SchedulingWeekday[];
  /** Sorted ascending. */
  periodIndices: number[];
  /** keyed by `${weekday}:${period_index}` → slot. */
  cells: Map<string, TimetableSlotView>;
  /** Used to surface "Period 1 · 9:00–9:45" labels in the leftmost
   * column. The first weekday's slot for a given period is enough
   * — period times are uniform across the week per BE invariants. */
  periodLabels: Map<number, { startMin: number; endMin: number }>;
}

function buildWeeklyGrid(slots: TimetableSlotView[]): BuiltGrid {
  const weekdaySet = new Set<SchedulingWeekday>();
  const periodSet = new Set<number>();
  const cells = new Map<string, TimetableSlotView>();
  const periodLabels = new Map<number, { startMin: number; endMin: number }>();
  for (const slot of slots) {
    weekdaySet.add(slot.weekday);
    periodSet.add(slot.period_index);
    cells.set(`${slot.weekday}:${slot.period_index}`, slot);
    if (!periodLabels.has(slot.period_index)) {
      periodLabels.set(slot.period_index, {
        startMin: slot.start_minute,
        endMin: slot.end_minute,
      });
    }
  }
  const weekdays = WEEKDAY_ORDER.filter((d) => weekdaySet.has(d));
  const periodIndices = Array.from(periodSet).sort((a, b) => a - b);
  return { weekdays, periodIndices, cells, periodLabels };
}

// ── Teacher view ─────────────────────────────────────────────────────

interface TeacherStat {
  teacherId: string;
  totalSlots: number;
  /** Slots per weekday — used for an at-a-glance workload sparkline. */
  perDay: Record<SchedulingWeekday, number>;
  /** Sum of (end-start) in minutes across the week. */
  totalMinutes: number;
}

function computeTeacherStats(slots: TimetableSlotView[]): TeacherStat[] {
  const byTeacher = new Map<string, TeacherStat>();
  for (const slot of slots) {
    let stat = byTeacher.get(slot.teacher_id);
    if (!stat) {
      stat = {
        teacherId: slot.teacher_id,
        totalSlots: 0,
        perDay: { MON: 0, TUE: 0, WED: 0, THU: 0, FRI: 0, SAT: 0, SUN: 0 },
        totalMinutes: 0,
      };
      byTeacher.set(slot.teacher_id, stat);
    }
    stat.totalSlots += 1;
    stat.perDay[slot.weekday] += 1;
    stat.totalMinutes += Math.max(0, slot.end_minute - slot.start_minute);
  }
  return Array.from(byTeacher.values()).sort((a, b) => b.totalSlots - a.totalSlots);
}

interface TeacherFilterBarProps {
  timetableId: string;
  teacherStats: TeacherStat[];
  selectedTeacherId: string | null;
  onSelect: (teacherId: string | null) => void;
}

function TeacherFilterBar({
  timetableId,
  teacherStats,
  selectedTeacherId,
  onSelect,
}: TeacherFilterBarProps) {
  if (teacherStats.length === 0) {
    return null;
  }
  return (
    <Card>
      <CardContent className="flex flex-wrap items-center gap-2 p-4">
        <span className="text-muted-foreground mr-1 text-xs font-medium uppercase tracking-wide">
          Teacher view
        </span>
        <button
          type="button"
          onClick={() => onSelect(null)}
          aria-pressed={selectedTeacherId === null}
          className={cn(
            "rounded-md border px-2.5 py-1 text-xs font-medium transition-colors",
            selectedTeacherId === null
              ? "border-primary bg-primary text-primary-foreground"
              : "border-border bg-background text-muted-foreground hover:bg-muted",
          )}
        >
          All teachers
        </button>
        {teacherStats.map((stat) => {
          const active = stat.teacherId === selectedTeacherId;
          // Each chip is a split control: the LEFT half toggles the
          // in-page filter (current G3 FE-3 behaviour). The RIGHT half
          // is a permalink to the teacher's standalone, printable
          // schedule page (G3 FE-4) — keeping both affordances in one
          // chip avoids cluttering the bar with parallel link rows.
          return (
            <span
              key={stat.teacherId}
              className={cn(
                "inline-flex items-stretch overflow-hidden rounded-md border text-xs font-medium",
                active
                  ? "border-primary"
                  : "border-border bg-background hover:bg-muted/50 text-muted-foreground",
              )}
              data-testid={`teacher-chip-${stat.teacherId}`}
            >
              <button
                type="button"
                onClick={() => onSelect(active ? null : stat.teacherId)}
                aria-pressed={active}
                className={cn(
                  "inline-flex items-center gap-1.5 px-2.5 py-1 transition-colors",
                  active
                    ? "bg-primary text-primary-foreground"
                    : "text-muted-foreground hover:bg-muted",
                )}
              >
                <span className="font-mono text-[11px]">{stat.teacherId}</span>
                <span
                  className={cn(
                    "rounded-sm px-1 text-[10px] tabular-nums",
                    active ? "bg-primary-foreground/15" : "bg-muted text-foreground/70",
                  )}
                >
                  {stat.totalSlots}
                </span>
              </button>
              <Link
                to={`/timetable/${encodeURIComponent(timetableId)}/teachers/${encodeURIComponent(stat.teacherId)}`}
                aria-label={`Open ${stat.teacherId}'s standalone schedule`}
                title="Open standalone schedule"
                data-testid={`teacher-chip-open-${stat.teacherId}`}
                className={cn(
                  "border-current/20 inline-flex items-center border-l px-1.5 transition-colors",
                  active
                    ? "bg-primary text-primary-foreground hover:bg-primary/90"
                    : "text-muted-foreground hover:bg-muted hover:text-foreground",
                )}
              >
                <ExternalLink aria-hidden="true" className="size-3" />
              </Link>
            </span>
          );
        })}
      </CardContent>
    </Card>
  );
}

interface WeeklyGridProps {
  grid: BuiltGrid;
  isReadOnly: boolean;
  highlightedTeacherId: string | null;
  onSelectSlot: (slot: TimetableSlotView) => void;
}

function WeeklyGrid({ grid, isReadOnly, highlightedTeacherId, onSelectSlot }: WeeklyGridProps) {
  if (grid.weekdays.length === 0 || grid.periodIndices.length === 0) {
    return (
      <Card>
        <CardContent className="text-muted-foreground flex min-h-[260px] flex-col items-center justify-center gap-3 text-center">
          <Sparkles aria-hidden="true" className="size-6 opacity-60" />
          <div>
            <p className="text-foreground font-medium">No slots yet</p>
            <p className="text-xs">
              This timetable hasn't been generated yet. Start a new schedule from the workspace, or
              check the source generation job for a conflict report.
            </p>
          </div>
        </CardContent>
      </Card>
    );
  }

  // Use a CSS grid template so every cell snaps to the same width
  // regardless of content. The first column is wider for the period
  // label; the rest use 1fr to share the remaining width evenly.
  const templateColumns = `120px repeat(${grid.weekdays.length}, minmax(140px, 1fr))`;

  return (
    <Card className="overflow-hidden">
      <CardHeader className="flex flex-row items-center justify-between gap-2 border-b">
        <div>
          <CardTitle className="text-base">Weekly schedule</CardTitle>
          <p className="text-muted-foreground mt-0.5 text-xs">
            {isReadOnly
              ? "Archived timetable — cells are read-only."
              : "Click any cell to override the assigned teacher, subject, or room."}
          </p>
        </div>
        <Badge variant="outline" className="font-normal">
          {grid.weekdays.length} day{grid.weekdays.length === 1 ? "" : "s"} ·{" "}
          {grid.periodIndices.length} period
          {grid.periodIndices.length === 1 ? "" : "s"}
        </Badge>
      </CardHeader>
      <CardContent className="p-0">
        <div className="overflow-x-auto">
          <div
            role="grid"
            aria-label="Weekly schedule"
            className="divide-border/60 grid divide-y"
            style={{
              gridTemplateColumns: templateColumns,
              minWidth: `${120 + grid.weekdays.length * 140}px`,
            }}
          >
            {/* Header row */}
            <div role="columnheader" className="bg-muted/40 px-3 py-2 text-xs font-medium" />
            {grid.weekdays.map((weekday) => (
              <div
                key={weekday}
                role="columnheader"
                className="bg-muted/40 border-border/60 border-l px-3 py-2 text-xs font-medium"
              >
                {WEEKDAY_SHORT[weekday]}
              </div>
            ))}
            {/* Period rows */}
            {grid.periodIndices.map((periodIdx) => {
              const label = grid.periodLabels.get(periodIdx);
              return (
                <React.Fragment key={periodIdx}>
                  <div role="rowheader" className="bg-muted/20 border-border/60 border-t px-3 py-3">
                    <div className="text-foreground text-xs font-semibold">
                      Period {periodIdx + 1}
                    </div>
                    {label ? (
                      <div className="text-muted-foreground mt-0.5 text-[10px] tabular-nums">
                        {formatMinute(label.startMin)} – {formatMinute(label.endMin)}
                      </div>
                    ) : null}
                  </div>
                  {grid.weekdays.map((weekday) => {
                    const slot = grid.cells.get(`${weekday}:${periodIdx}`);
                    const dimmed =
                      highlightedTeacherId !== null &&
                      slot !== undefined &&
                      slot.teacher_id !== highlightedTeacherId;
                    return (
                      <SlotCell
                        key={`${weekday}:${periodIdx}`}
                        slot={slot}
                        isReadOnly={isReadOnly}
                        dimmed={dimmed}
                        onSelect={() => slot && onSelectSlot(slot)}
                      />
                    );
                  })}
                </React.Fragment>
              );
            })}
          </div>
        </div>
      </CardContent>
    </Card>
  );
}

interface SlotCellProps {
  slot: TimetableSlotView | undefined;
  isReadOnly: boolean;
  dimmed: boolean;
  onSelect: () => void;
}

function SlotCell({ slot, isReadOnly, dimmed, onSelect }: SlotCellProps) {
  if (!slot) {
    return (
      <div
        role="gridcell"
        className="text-muted-foreground/40 border-border/60 border-l border-t px-3 py-3 text-center text-[10px]"
        aria-label="Empty slot"
      >
        —
      </div>
    );
  }
  const interactive = !isReadOnly;
  return (
    <button
      type="button"
      role="gridcell"
      onClick={interactive ? onSelect : undefined}
      disabled={!interactive}
      aria-label={`${slot.subject_code} for ${slot.section_grade}-${slot.section_id} on ${WEEKDAY_SHORT[slot.weekday]} period ${slot.period_index + 1}`}
      className={cn(
        "border-border/60 border-l border-t px-3 py-3 text-left transition-colors",
        interactive && "hover:bg-muted/50 focus-visible:bg-muted/60",
        slot.is_lab_period && "bg-amber-500/5",
        slot.manual_override && "bg-sky-500/5",
        !interactive && "cursor-default",
        dimmed && "opacity-30",
      )}
    >
      <div className="text-foreground truncate text-xs font-semibold">{slot.subject_code}</div>
      <div className="text-muted-foreground mt-0.5 truncate text-[10px]">
        {slot.section_grade} · {slot.section_id}
      </div>
      <div className="text-muted-foreground mt-1 truncate text-[10px]">{slot.teacher_id}</div>
      {slot.room_id ? (
        <div className="text-muted-foreground/70 mt-0.5 truncate text-[10px]">
          Rm. {slot.room_id}
        </div>
      ) : null}
      <div className="mt-1 flex flex-wrap gap-1">
        {slot.is_lab_period ? (
          <Badge
            variant="outline"
            className="border-amber-500/40 text-[9px] text-amber-700 dark:text-amber-400"
          >
            Lab
          </Badge>
        ) : null}
        {slot.manual_override ? (
          <Badge
            variant="outline"
            className="border-sky-500/40 text-[9px] text-sky-700 dark:text-sky-400"
          >
            Override
          </Badge>
        ) : null}
      </div>
    </button>
  );
}

// ── Summary aside ────────────────────────────────────────────────────

interface SummaryAsideProps {
  timetableId: string;
  totalSlots: number;
  teacherCount: number;
  sectionCount: number;
  objectiveScore: number | null;
  sourceJobId: string | null;
  publishedAt: string | null;
  archivedAt: string | null;
  selectedTeacher: TeacherStat | null;
}

function SummaryAside({
  timetableId,
  totalSlots,
  teacherCount,
  sectionCount,
  objectiveScore,
  sourceJobId,
  publishedAt,
  archivedAt,
  selectedTeacher,
}: SummaryAsideProps) {
  return (
    <Card className="self-start">
      <CardHeader>
        <CardTitle className="text-base">Summary</CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        <SummaryRow label="Sections" value={sectionCount.toString()} />
        <SummaryRow label="Teachers" value={teacherCount.toString()} />
        <SummaryRow label="Slots" value={totalSlots.toString()} />
        <SummaryRow
          label="Objective score"
          value={objectiveScore === null ? "—" : objectiveScore.toFixed(2)}
        />
        {publishedAt ? (
          <SummaryRow label="Published" value={new Date(publishedAt).toLocaleString()} />
        ) : null}
        {archivedAt ? (
          <SummaryRow label="Archived" value={new Date(archivedAt).toLocaleString()} />
        ) : null}
        {selectedTeacher ? (
          <div className="border-border/60 space-y-2 border-t pt-3">
            <p className="text-muted-foreground text-[10px] uppercase tracking-wide">
              Selected teacher
            </p>
            <p className="text-foreground font-mono text-xs">{selectedTeacher.teacherId}</p>
            <SummaryRow label="Teaching slots" value={selectedTeacher.totalSlots.toString()} />
            <SummaryRow
              label="Weekly hours"
              value={(selectedTeacher.totalMinutes / 60).toFixed(1)}
            />
            <div className="text-muted-foreground grid grid-cols-7 gap-1 text-center text-[9px] tabular-nums">
              {(["MON", "TUE", "WED", "THU", "FRI", "SAT", "SUN"] as SchedulingWeekday[]).map(
                (d) => (
                  <div key={d}>
                    <div className="text-[8px] uppercase">{WEEKDAY_SHORT[d].slice(0, 1)}</div>
                    <div className="text-foreground mt-0.5 font-medium">
                      {selectedTeacher.perDay[d]}
                    </div>
                  </div>
                ),
              )}
            </div>
            <Button
              variant="outline"
              size="sm"
              asChild
              className="w-full"
              data-testid="aside-open-teacher-page"
            >
              <Link
                to={`/timetable/${encodeURIComponent(timetableId)}/teachers/${encodeURIComponent(selectedTeacher.teacherId)}`}
              >
                <ExternalLink aria-hidden="true" className="size-3.5" />
                Open standalone page
              </Link>
            </Button>
          </div>
        ) : null}
        {sourceJobId ? (
          <div className="border-border/60 border-t pt-3">
            <p className="text-muted-foreground text-[10px] uppercase tracking-wide">Produced by</p>
            <p className="text-foreground mt-1 font-mono text-xs">
              {/* The job-detail route ships in G3 FE-1c; until then
                  we show the id so the IA can correlate with the
                  jobs panel by hand. */}
              <code>{sourceJobId.slice(0, 12)}…</code>
            </p>
          </div>
        ) : null}
        <div className="border-border/60 text-muted-foreground border-t pt-3 text-[11px]">
          <CalendarRange aria-hidden="true" className="mr-1.5 inline size-3" />
          Need a different schedule? Start a new generation from the workspace.
        </div>
      </CardContent>
    </Card>
  );
}

function SummaryRow({ label, value }: { label: string; value: string }) {
  return (
    <div className="flex items-baseline justify-between gap-3">
      <span className="text-muted-foreground text-xs">{label}</span>
      <span className="text-foreground text-sm font-medium tabular-nums">{value}</span>
    </div>
  );
}

// ── Slot override sheet ──────────────────────────────────────────────

interface SlotOverrideSheetProps {
  slot: TimetableSlotView | null;
  onClose: () => void;
  onSubmit: (payload: ApplyManualOverrideRequest) => Promise<void>;
  submitting: boolean;
}

function SlotOverrideSheet({ slot, onClose, onSubmit, submitting }: SlotOverrideSheetProps) {
  const [teacherId, setTeacherId] = React.useState("");
  const [subjectCode, setSubjectCode] = React.useState("");
  const [roomId, setRoomId] = React.useState("");
  const [clearRoom, setClearRoom] = React.useState(false);
  const [error, setError] = React.useState<string | null>(null);

  React.useEffect(() => {
    setTeacherId("");
    setSubjectCode("");
    setRoomId("");
    setClearRoom(false);
    setError(null);
  }, [slot?.id]);

  const handleSubmit: React.FormEventHandler<HTMLFormElement> = async (e) => {
    e.preventDefault();
    setError(null);
    if (!slot) return;
    const payload: ApplyManualOverrideRequest = {};
    if (teacherId.trim()) payload.teacher_id = teacherId.trim();
    if (subjectCode.trim()) payload.subject_code = subjectCode.trim();
    if (clearRoom) {
      payload.clear_room = true;
    } else if (roomId.trim()) {
      payload.room_id = roomId.trim();
    }
    if (Object.keys(payload).length === 0) {
      setError("Set at least one of teacher, subject, room, or check 'Clear room'.");
      return;
    }
    if (payload.clear_room && payload.room_id) {
      setError("Choose either a new room OR 'Clear room', not both.");
      return;
    }
    try {
      await onSubmit(payload);
    } catch (caught) {
      const message = caught instanceof Error ? caught.message : String(caught);
      setError(message || "Override failed. Try again.");
    }
  };

  return (
    <Sheet open={Boolean(slot)} onOpenChange={(open) => !open && onClose()}>
      <SheetContent side="right" className="sm:max-w-md">
        <SheetHeader>
          <SheetTitle className="flex items-center gap-2">
            <Edit3 aria-hidden="true" className="size-4" />
            Override slot
          </SheetTitle>
          <SheetDescription>
            {slot
              ? `${WEEKDAY_SHORT[slot.weekday]} · Period ${slot.period_index + 1} · ${slot.section_grade}-${slot.section_id}`
              : ""}
          </SheetDescription>
        </SheetHeader>
        {slot ? (
          <form onSubmit={handleSubmit} className="mt-5 space-y-4" aria-busy={submitting}>
            <div className="bg-muted/40 border-border/60 rounded-md border p-3 text-xs">
              <p className="text-muted-foreground">Current</p>
              <p className="text-foreground mt-1 font-medium">
                {slot.subject_code} · {slot.teacher_id}
                {slot.room_id ? ` · Rm. ${slot.room_id}` : ""}
              </p>
            </div>
            <div>
              <Label htmlFor="override-teacher">Teacher (user id)</Label>
              <Input
                id="override-teacher"
                value={teacherId}
                onChange={(e) => setTeacherId(e.target.value)}
                placeholder={slot.teacher_id}
                autoComplete="off"
              />
            </div>
            <div>
              <Label htmlFor="override-subject">Subject code</Label>
              <Input
                id="override-subject"
                value={subjectCode}
                onChange={(e) => setSubjectCode(e.target.value)}
                placeholder={slot.subject_code}
                autoComplete="off"
              />
            </div>
            <div>
              <Label htmlFor="override-room">Room id</Label>
              <Input
                id="override-room"
                value={roomId}
                onChange={(e) => setRoomId(e.target.value)}
                placeholder={slot.room_id ?? "(no room set)"}
                autoComplete="off"
                disabled={clearRoom}
              />
              <label className="text-muted-foreground mt-2 flex items-center gap-2 text-xs">
                <input
                  type="checkbox"
                  className="accent-foreground size-3.5"
                  checked={clearRoom}
                  onChange={(e) => setClearRoom(e.target.checked)}
                />
                Clear the assigned room
              </label>
            </div>
            {error ? (
              <div
                role="alert"
                className="bg-destructive/10 text-destructive border-destructive/30 flex items-start gap-2 rounded-md border p-3 text-xs"
              >
                <ShieldAlert aria-hidden="true" className="size-4" />
                <p>{error}</p>
              </div>
            ) : null}
            <div className="flex items-center justify-end gap-2 pt-2">
              <SheetClose asChild>
                <Button type="button" variant="ghost" size="sm">
                  Cancel
                </Button>
              </SheetClose>
              <Button type="submit" size="sm" disabled={submitting}>
                <ChevronRight aria-hidden="true" className="size-4" />
                {submitting ? "Saving…" : "Save override"}
              </Button>
            </div>
          </form>
        ) : null}
      </SheetContent>
    </Sheet>
  );
}

// ── Loading + error states ───────────────────────────────────────────

function DetailSkeleton() {
  return (
    <div className="space-y-6">
      <div className="bg-muted h-7 w-64 animate-pulse rounded" />
      <div className="bg-muted h-4 w-96 animate-pulse rounded" />
      <Card>
        <CardContent className="grid gap-3 p-6">
          {Array.from({ length: 6 }).map((_, idx) => (
            <div key={idx} className="bg-muted h-10 animate-pulse rounded" />
          ))}
        </CardContent>
      </Card>
    </div>
  );
}

function DetailError({ onRetry }: { onRetry: () => void }) {
  return (
    <Card>
      <CardContent className="flex min-h-[260px] flex-col items-center justify-center gap-3 text-center">
        <ShieldAlert aria-hidden="true" className="text-destructive size-8" />
        <div>
          <p className="text-foreground font-medium">Couldn't load this timetable</p>
          <p className="text-muted-foreground text-xs">
            It may have been archived or you may not have access. Try refreshing or head back to the
            workspace.
          </p>
        </div>
        <div className="flex gap-2">
          <Button size="sm" variant="outline" onClick={onRetry}>
            <RefreshCw aria-hidden="true" className="size-4" />
            Retry
          </Button>
          <Button size="sm" variant="ghost" asChild>
            <Link to="/timetable">Back to workspace</Link>
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}

// ── Helpers ──────────────────────────────────────────────────────────

const STATUS_PILL: Record<TimetableStatusValue, StatusPillStatus> = {
  draft: "pending",
  published: "active",
  archived: "archived",
};

const STATUS_LABEL: Record<TimetableStatusValue, string> = {
  draft: "Draft",
  published: "Published",
  archived: "Archived",
};

function formatMinute(minute: number): string {
  const h = Math.floor(minute / 60);
  const m = minute % 60;
  const hh = ((h + 11) % 12) + 1;
  const mm = m.toString().padStart(2, "0");
  const ampm = h < 12 ? "AM" : "PM";
  return `${hh}:${mm} ${ampm}`;
}
